import { gsap } from 'gsap';
import { getKeyboardFocusableElements } from '../utils';

export default class ProgramEvent {
	constructor(elm) {
		this.elm = elm;

		this.breakpoint = window.matchMedia('(min-width:992px)');

		this.isOpen = false;
		this.isAnimating = false;

		this.collapse = this.elm.querySelector('[data-event-collapse]');

		this.eventToggleButton = this.elm.querySelector('[data-event-toggle-button]');
		this.eventToggleButton.addEventListener('click', this.toggleDetails.bind(this));

		this.closeAreas = this.elm.querySelectorAll('[data-event-close-area]');
		this.closeAreas.forEach((elm) => {
			elm.addEventListener('click', this.toggleDetails.bind(this));
		});

		// Set initial tabindex
		this.getKeyboardFocusableElements = getKeyboardFocusableElements(this.collapse);
		this.getKeyboardFocusableElements.forEach((element) => (element.tabIndex = -1));

		this.firstFocusableEl = this.getKeyboardFocusableElements[0];
		this.lastFocusableEl = this.getKeyboardFocusableElements[this.getKeyboardFocusableElements.length - 1];
	}

	toggleDetails() {
		if (this.isAnimating) {
			return;
		}
		if (this.isOpen) {
			this.closeDetails();
		} else {
			this.openDetails();
		}
	}

	openDetails(instant) {
		if (this.isOpen) {
			return;
		}

		const tl = gsap.timeline({
			delay: 0,
			onStart: () => {
				this.isAnimating = true;
				this.elm.classList.add('is-open');
			},
			onComplete: () => {
				this.isAnimating = false;
				this.isOpen = true;
			},
		});

		tl.fromTo(
			this.collapse,
			{
				height: this.breakpoint.matches ? '8rem' : 0,
			},
			{
				height: this.collapse.scrollHeight,
				clearProps: 'height',
				ease: 'power2.inOut',
				duration: instant ? 0 : 1,
			},
			0
		);

		tl.fromTo(
			this.collapse,
			{
				opacity: 0,
			},
			{
				opacity: 1,
				ease: 'sine.inOut',
				duration: instant ? 0 : 0.8,
			},
			0.6
		);

		this.trapFocus();
	}

	closeDetails(instant) {
		if (!this.isOpen) {
			return;
		}

		const tl = gsap.timeline({
			onStart: () => {
				this.isAnimating = true;
				this.elm.classList.remove('is-open');
			},
			onComplete: () => {
				this.isAnimating = false;
				this.isOpen = false;
			},
		});

		tl.fromTo(
			this.collapse,
			{
				opacity: 1,
			},
			{
				opacity: 0,
				ease: 'sine.inOut',
				duration: instant ? 0 : 0.4,
			}
		);

		tl.fromTo(
			this.collapse,
			{
				height: this.collapse.scrollHeight,
			},
			{
				height: this.breakpoint.matches ? '8rem' : 0,
				clearProps: 'height',
				ease: 'power2.inOut',
				duration: instant ? 0 : 0.8,
			},
			0.2
		);

		this.removeTrapFocus();
	}

	trapFocus() {
		this.getKeyboardFocusableElements.forEach((element) => element.removeAttribute('tabindex'));

		this.tabfocussed = (event) => this.tabFocus(event);
		document.addEventListener('keydown', this.tabfocussed);
	}

	tabFocus(e) {
		if (e.key === 'Escape') {
			this.closeDetails();
			this.eventToggleButton.focus();
		}

		const KEYCODE_TAB = 9;
		const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			if (document.activeElement === this.firstFocusableEl) {
				this.lastFocusableEl.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === this.lastFocusableEl) {
				this.firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	}

	removeTrapFocus(element) {
		this.getKeyboardFocusableElements.forEach((element) => (element.tabIndex = -1));
		document.removeEventListener('keydown', this.tabfocussed);
	}

	destroy() {
		this.removeTrapFocus();
	}
}
